<template>
<div class="language-export">
  <h3>{{ $t('vform.language') }} Export</h3>
  <div class="lighter">{{ $t('vform.languageExportHint') }}</div>
  <label class="vform-label white mt-2 mb-0">{{ $t('vform.sourceLanguage') }}</label>
  <language-selector
      :use-plain-language="true"
      class="mr-3 language-selector-bottom-bar align-self-center no-shadow"
      open-direction="top"
      @update="(newlang) => {sourceLanguage = newlang}"
  />
  <label class="vform-label white mt-2 mb-0">{{ $t('vform.targetLanguage') }}</label>
  <language-selector
      :use-plain-language="true"
      class="mr-3 language-selector-bottom-bar align-self-center no-shadow"
      open-direction="top"
      @update="(newlang) => {targetLanguage = newlang}"
  />
  <div @click="exportLanguage" class="settings-button d-inline-block mt-3">
    {{ $t('vform.exportLanguage') }}
  </div>
</div>
</template>

<script>
import LanguageSelector from "@/components/forms/LanguageSelector.vue";
import {PlainLanguages, Languages} from "@/enum";
import LanguageHandler from "./LanguageHandler";
import DateMixin from "@/components/mixins/DateMixin.js";

export default {
  name: "LanguageExport",
  components: {LanguageSelector},
  mixins: [DateMixin],
  props: {
    config: {type: Object, required: false, default: () => ({})},
  },
  data() {
    return {
      LanguageHandler,
      Languages,
      sourceLanguage: null,
      targetLanguage: null,
      translatedStrings: {},
    }
  },
  computed: {
    getTranslatedLanguageKeys() {
      const lh = new LanguageHandler(this.config);
      return lh.getTranslatedLanguageKeys();
    },
    getNonTranslatedLanguageKeys() {
      const translated = this.getTranslatedLanguageKeys;
      return Object.keys(PlainLanguages).filter(lang => !translated.includes(lang));
    }
  },
  methods: {
    exportLanguage() {
      this.translatedStrings = {};
      const lh = new LanguageHandler(this.config);
      this.translatedStrings = lh.getLanguageStringsWithTheirUuids(this.sourceLanguage, this.targetLanguage);
      this.exportToPoFile();
    },
    poEscape(str) {
      return String(str)
          .replace(/\r\n/g, '\n')    // normalize
          .replace(/\r/g, '\n')
          .replace(/\\/g, '\\\\')    // backslashes first
          .replace(/"/g, '\\"')      // quotes
          .replace(/\t/g, '\\t')     // tabs
          .replace(/\n/g, '\\n');    // real newlines -> \n
    },
    getPoHeader() {
      let header = 'msgid ""\nmsgstr ""\n';
      header += '"MIME-Version: 1.0\\n"\n';
      header += '"Content-Type: text/plain; charset=UTF-8\\n"\n';
      header += '"Content-Transfer-Encoding: 8bit\\n"\n';
      if (this.targetLanguage) {
        // The selector emits display names; configs can also use enum keys or codes.
        const plainLanguage = PlainLanguages[this.targetLanguage]
          || Object.values(PlainLanguages).find(language =>
            language.name === this.targetLanguage || language.code === this.targetLanguage);
        const localeKey = Object.keys(Languages).find(key =>
          key === this.targetLanguage || Languages[key].name === this.targetLanguage);
        const languageCode = plainLanguage ? plainLanguage.code : (localeKey || this.targetLanguage);
        // gettext uses zh_CN for Simplified Chinese and underscores for locales.
        const poLanguage = languageCode === 'zh-Hans' ? 'zh_CN' : languageCode.replace(/-/g, '_');
        header += `"Language: ${this.poEscape(poLanguage)}\\n"\n`;
      }
      return header + '\n';
    },
    exportToPoFile() {
      let poContent = '';
      poContent += this.getPoHeader();
      Object.keys(this.translatedStrings).forEach(uuid => {
        const original = this.translatedStrings[uuid].original;
        const translated = this.translatedStrings[uuid].translated;
        // build po file content
        poContent += `msgctxt "${uuid}"\n`;
        poContent += `msgid "${this.poEscape(original)}"\n`;
        poContent += `msgstr "${this.poEscape(translated)}"\n\n`;
      });
      const filename = `${this.currrentDateTimeFormattedForFileNames()}_${this.sourceLanguage || 'all'}-to-${this.targetLanguage || 'all'}.po`;
      this.triggerDownload(filename, poContent);
    },
    triggerDownload(filename, content) {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
</script>

<style lang="scss">
.language-export .language-selector {
  background-color: var(--vform-editor-ui-secondary-color) !important;
  height: auto;
  padding-top: 2px !important;
}
</style>
